<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import SourceInput from '~/components/ui/SourceInput.vue'
import { sanitizeOrgRichText } from '~/composables/ui/sanitizeOrgRichText'

type LinkKind = 'web' | 'mail' | 'tel'

const props = defineProps<{
  editor: Editor | undefined
  canEdit: boolean
  open: boolean
  openRequest: number
  anchorElement: HTMLElement | null
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const LINK_POPOVER_WIDTH = 280
const LINK_POPOVER_GAP = 8
const LINK_POPOVER_MARGIN = 8
const LINK_POPOVER_FALLBACK_HEIGHT = 260

const linkKind = ref<LinkKind>('web')
const linkText = ref('')
const linkHref = ref('')
const linkFrom = ref(0)
const linkTo = ref(0)
const linkPopoverStyle = ref<Record<string, string>>({})
const linkPopoverEl = ref<HTMLElement | null>(null)
const linkPopoverHost = ref<HTMLElement | string>('body')

function detectLinkKind(href: string): LinkKind {
  if (href.startsWith('mailto:')) {
    return 'mail'
  }
  if (href.startsWith('tel:')) {
    return 'tel'
  }
  return 'web'
}

function normalizeLinkHref(raw: string, kind: LinkKind): string {
  const value = raw.trim()
  if (!value) {
    return ''
  }
  if (kind === 'mail') {
    const email = value.replace(/^mailto:/i, '').trim()
    return email ? `mailto:${email}` : ''
  }
  if (kind === 'tel') {
    const phone = value.replace(/^tel:/i, '').replace(/[^\d+]/g, '')
    return phone ? `tel:${phone}` : ''
  }
  if (/^(https?:\/\/|mailto:|tel:)/i.test(value)) {
    return value
  }
  return `https://${value}`
}

function linkPlaceholder(kind: LinkKind): string {
  if (kind === 'mail') {
    return 'mailto:prenom.nom@enedis.fr'
  }
  if (kind === 'tel') {
    return 'tel:+33600000000'
  }
  return 'https://'
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
}

function closeLinkPopover(): void {
  emit('update:open', false)
}

function resolveLinkPopoverHost(): HTMLElement | string {
  const layer = props.anchorElement?.closest('[data-dismissable-layer]')
  return layer instanceof HTMLElement ? layer : 'body'
}

function isEventInsideLinkPopover(event: Event): boolean {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : []
  if (path.some(function isLinkNode(node) {
    return node instanceof Element && (
      node.classList.contains('source-rte-link-pop')
      || node.hasAttribute('data-rte-link-tool')
    )
  })) {
    return true
  }
  const target = event.target
  if (target instanceof Node && linkPopoverEl.value?.contains(target)) {
    return true
  }
  return target instanceof Element
    && Boolean(target.closest('.source-rte-link-pop, [data-rte-link-tool]'))
}

function onDocumentPointerDown(event: PointerEvent): void {
  if (!props.open) {
    return
  }
  if (isEventInsideLinkPopover(event)) {
    return
  }
  closeLinkPopover()
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.open) {
    event.preventDefault()
    closeLinkPopover()
  }
}

function getViewportBox(): { left: number, top: number, width: number, height: number } {
  const visual = window.visualViewport
  if (visual) {
    return {
      left: visual.offsetLeft,
      top: visual.offsetTop,
      width: visual.width,
      height: visual.height,
    }
  }
  return {
    left: 0,
    top: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

function getClampBox(): { left: number, top: number, width: number, height: number } {
  const viewport = getViewportBox()
  const host = linkPopoverHost.value
  if (!(host instanceof HTMLElement)) {
    return viewport
  }
  const rect = host.getBoundingClientRect()
  const left = Math.max(viewport.left, rect.left)
  const top = Math.max(viewport.top, rect.top)
  const right = Math.min(viewport.left + viewport.width, rect.right)
  const bottom = Math.min(viewport.top + viewport.height, rect.bottom)
  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  }
}

function toHostPosition(left: number, top: number): { left: number, top: number } {
  const host = linkPopoverHost.value
  if (!(host instanceof HTMLElement)) {
    return { left, top }
  }
  const rect = host.getBoundingClientRect()
  return {
    left: left - rect.left,
    top: top - rect.top,
  }
}

function placeLinkPopover(instance: Editor): void {
  const { from, to } = instance.state.selection
  const endPos = to > from ? to - 1 : from
  const start = instance.view.coordsAtPos(from)
  const end = instance.view.coordsAtPos(endPos)
  const selectionTop = Math.min(start.top, end.top)
  const selectionBottom = Math.max(start.bottom, end.bottom)
  const viewport = getClampBox()
  const popover = linkPopoverEl.value
  const width = Math.min(
    popover?.offsetWidth || LINK_POPOVER_WIDTH,
    Math.max(0, viewport.width - 2 * LINK_POPOVER_MARGIN),
  )
  const contentHeight = popover?.scrollHeight || LINK_POPOVER_FALLBACK_HEIGHT
  const minLeft = viewport.left + LINK_POPOVER_MARGIN
  const maxLeft = viewport.left + viewport.width - width - LINK_POPOVER_MARGIN
  const left = Math.min(Math.max(minLeft, Math.min(start.left, end.left)), Math.max(minLeft, maxLeft))
  const minTop = viewport.top + LINK_POPOVER_MARGIN
  const maxBottom = viewport.top + viewport.height - LINK_POPOVER_MARGIN
  const below = selectionBottom + LINK_POPOVER_GAP
  const availableBelow = maxBottom - below
  const availableAbove = selectionTop - LINK_POPOVER_GAP - minTop
  const fitsBelow = contentHeight <= availableBelow
  const fitsAbove = contentHeight <= availableAbove

  let top: number
  let maxHeight: number
  if (fitsBelow) {
    top = below
    maxHeight = availableBelow
  } else if (fitsAbove) {
    maxHeight = availableAbove
    top = selectionTop - LINK_POPOVER_GAP - Math.min(contentHeight, maxHeight)
  } else if (availableAbove > availableBelow) {
    maxHeight = Math.max(0, availableAbove)
    top = minTop
  } else {
    top = below
    maxHeight = Math.max(0, availableBelow)
  }

  if (maxHeight < 72) {
    maxHeight = Math.max(72, maxBottom - minTop)
    top = minTop
  }

  const displayedHeight = Math.min(contentHeight, maxHeight)
  top = Math.min(Math.max(minTop, top), Math.max(minTop, maxBottom - displayedHeight))
  const positioned = toHostPosition(left, top)
  linkPopoverStyle.value = {
    top: `${positioned.top}px`,
    left: `${positioned.left}px`,
    maxWidth: `${width}px`,
    maxHeight: `${displayedHeight}px`,
  }
}

function schedulePlaceLinkPopover(): void {
  void nextTick(function placeWhenReady() {
    const instance = props.editor
    if (instance && props.open) {
      placeLinkPopover(instance)
    }
  })
}

function run(command: (instance: Editor) => void): void {
  const instance = props.editor
  if (!instance || !props.canEdit) {
    return
  }
  command(instance)
}

function openForSelection(): void {
  const instance = props.editor
  if (!instance || !props.canEdit) {
    return
  }
  const { from, to } = instance.state.selection
  const previous = instance.getAttributes('link').href
  const href = typeof previous === 'string' ? previous : ''
  linkFrom.value = from
  linkTo.value = to
  linkText.value = instance.state.doc.textBetween(from, to, ' ')
  linkKind.value = detectLinkKind(href)
  linkHref.value = href || (linkKind.value === 'web' ? 'https://' : '')
  linkPopoverHost.value = resolveLinkPopoverHost()
  emit('update:open', true)
  schedulePlaceLinkPopover()
}

function webHrefFromStrippedValue(stripped: string): string {
  if (!stripped) return 'https://'
  if (stripped.startsWith('http')) return stripped
  return `https://${stripped}`
}

function setLinkKind(kind: LinkKind): void {
  const current = linkHref.value.trim()
  const stripped = current.replace(/^(https?:\/\/|mailto:|tel:)/i, '')
  linkKind.value = kind
  if (kind === 'mail') {
    linkHref.value = stripped ? `mailto:${stripped}` : 'mailto:'
    return
  }
  if (kind === 'tel') {
    linkHref.value = stripped ? `tel:${stripped}` : 'tel:'
    return
  }
  linkHref.value = webHrefFromStrippedValue(stripped)
}

function applyLink(): void {
  const href = normalizeLinkHref(linkHref.value, linkKind.value)
  const text = linkText.value.trim()
  run(function writeLink(instance) {
    const from = linkFrom.value
    const to = linkTo.value
    if (!href) {
      instance.chain().focus().setTextSelection({ from, to }).unsetLink().run()
      return
    }
    if (from === to) {
      const label = escapeHtml(text || href.replace(/^(https?:\/\/|mailto:|tel:)/i, ''))
      instance
        .chain()
        .focus()
        .insertContent(sanitizeOrgRichText(`<a href="${escapeHtml(href)}">${label}</a>`))
        .run()
      return
    }
    const selected = instance.state.doc.textBetween(from, to, ' ')
    if (text && text !== selected) {
      instance
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .insertContent(sanitizeOrgRichText(`<a href="${escapeHtml(href)}">${escapeHtml(text)}</a>`))
        .run()
      return
    }
    instance.chain().focus().setTextSelection({ from, to }).extendMarkRange('link').setLink({ href }).run()
  })
  closeLinkPopover()
}

function removeLink(): void {
  run(function unsetCurrentLink(instance) {
    instance
      .chain()
      .focus()
      .setTextSelection({ from: linkFrom.value, to: linkTo.value })
      .extendMarkRange('link')
      .unsetLink()
      .run()
  })
  closeLinkPopover()
}

watch(
  function watchOpenRequest() {
    return props.openRequest
  },
  function onOpenRequested(requestId) {
    if (requestId < 1) {
      return
    }
    openForSelection()
  },
)

watch(
  function watchPopoverOpen() {
    return props.open
  },
  function onPopoverOpenChanged(open, _wasOpen, onCleanup) {
    if (!open) {
      return
    }
    function reposition(): void {
      const instance = props.editor
      if (instance && props.open) {
        placeLinkPopover(instance)
      }
    }
    window.addEventListener('resize', reposition)
    document.addEventListener('scroll', reposition, true)
    window.visualViewport?.addEventListener('resize', reposition)
    window.visualViewport?.addEventListener('scroll', reposition)
    void nextTick(reposition)
    onCleanup(function removeRepositionListeners() {
      window.removeEventListener('resize', reposition)
      document.removeEventListener('scroll', reposition, true)
      window.visualViewport?.removeEventListener('resize', reposition)
      window.visualViewport?.removeEventListener('scroll', reposition)
    })
  },
)

onMounted(function bindPopoverDismiss() {
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  document.addEventListener('keydown', onDocumentKeydown)
})

onBeforeUnmount(function unbindPopoverDismiss() {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  document.removeEventListener('keydown', onDocumentKeydown)
})
</script>

<template>
  <Teleport :to="linkPopoverHost">
    <div
      v-if="open && canEdit"
      ref="linkPopoverEl"
      class="source-rte-link-pop"
      role="dialog"
      aria-label="Insérer un lien"
      :style="linkPopoverStyle"
      @pointerdown.stop
      @mousedown.stop
    >
      <SourceInput
        v-model="linkText"
        label="Texte"
        size="small"
        placeholder="Libellé affiché"
      />
      <SourceInput
        v-model="linkHref"
        label="Lien"
        size="small"
        :placeholder="linkPlaceholder(linkKind)"
      />
      <div class="source-rte-link-pop__wizard" role="group" aria-label="Type de lien">
        <SourceButton
          :type="linkKind === 'web' ? 'primary' : 'tertiary'"
          size="xsmall"
          icon="link"
          aria-label="Lien web"
          :aria-pressed="linkKind === 'web'"
          @click="setLinkKind('web')"
        />
        <SourceButton
          :type="linkKind === 'mail' ? 'primary' : 'tertiary'"
          size="xsmall"
          icon="mail"
          aria-label="E-mail"
          :aria-pressed="linkKind === 'mail'"
          @click="setLinkKind('mail')"
        />
        <SourceButton
          :type="linkKind === 'tel' ? 'primary' : 'tertiary'"
          size="xsmall"
          icon="call"
          aria-label="Téléphone"
          :aria-pressed="linkKind === 'tel'"
          @click="setLinkKind('tel')"
        />
      </div>
      <div class="source-rte-link-pop__actions">
        <SourceButton text="Retirer" type="tertiary" size="xsmall" @click="removeLink" />
        <SourceButton text="Appliquer" type="primary" size="xsmall" @click="applyLink" />
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
@use '~/assets/scss/partials/components/source-rich-text-editor-link-pop';
</style>
